import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';

/** Etat UI : montants / poids en texte libre pour permettre la saisie "9," puis "9,50". */
export type RateBracketRow = {
  id: string;
  weight_min_kg: string;
  weight_max_kg: string;
  carrier_price_eur: string;
  sherlook_fee_type: 'percent' | 'fixed';
  sherlook_fee_input: string;
  category_ids: number[];
  /** Options de type (ex. Chaussures) pour affiner la categorie. */
  type_values: string[];
};

export type CategoryOption = { id: number; name: string; typeOptions?: string[] };

function centsToEuroInput(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',');
}

function euroInputToCents(value: string): number {
  const normalized = value.replace(',', '.').trim();
  if (normalized === '' || normalized === '.' || normalized === ',') return 0;
  const n = Number.parseFloat(normalized);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 100);
}

function parseDecimalInput(value: string): number {
  const normalized = value.replace(',', '.').trim();
  if (normalized === '' || normalized === '.' || normalized === ',') return 0;
  const n = Number.parseFloat(normalized);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/** Accepte chiffres + une seule virgule/point (saisie en cours). */
function sanitizeDecimalTyping(raw: string): string {
  let value = raw.replace(/[^\d.,]/g, '').replace(/\./g, ',');
  const firstComma = value.indexOf(',');
  if (firstComma !== -1) {
    value =
      value.slice(0, firstComma + 1) + value.slice(firstComma + 1).replace(/,/g, '');
  }
  return value;
}

function computeFeeCents(
  carrierPriceCents: number,
  feeType: 'percent' | 'fixed',
  feeInput: string
): number {
  if (feeType === 'percent') {
    const pct = parseDecimalInput(feeInput);
    return Math.max(0, Math.round((carrierPriceCents * pct) / 100));
  }
  return euroInputToCents(feeInput);
}

function newBracketId(): string {
  return Math.random().toString(16).slice(2, 10);
}

function typeOptionsForCategories(
  categories: CategoryOption[],
  categoryIds: number[]
): string[] {
  const selected = categories.filter((c) => categoryIds.includes(c.id));
  const options = new Set<string>();
  for (const cat of selected) {
    for (const opt of cat.typeOptions ?? []) {
      const trimmed = opt.trim();
      if (trimmed) options.add(trimmed);
    }
  }
  return Array.from(options).sort((a, b) => a.localeCompare(b, 'fr'));
}

type Props = {
  carrierLabel: string;
  brackets: RateBracketRow[];
  categories: CategoryOption[];
  onChange: (next: RateBracketRow[]) => void;
};

export default function DeliveryRateBracketsEditor({
  carrierLabel,
  brackets,
  categories,
  onChange,
}: Props) {
  const [expandedId, setExpandedId] = useState<string | false>(false);

  useEffect(() => {
    if (expandedId && !brackets.some((row) => row.id === expandedId)) {
      setExpandedId(false);
    }
  }, [brackets, expandedId]);

  const updateRow = (id: string, patch: Partial<RateBracketRow>) => {
    onChange(brackets.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const removeRow = (id: string) => {
    onChange(brackets.filter((row) => row.id !== id));
    if (expandedId === id) setExpandedId(false);
  };

  const addRow = () => {
    const lastMax = brackets.reduce(
      (max, row) => Math.max(max, parseDecimalInput(row.weight_max_kg)),
      0
    );
    const nextMin = lastMax;
    const nextMax = lastMax > 0 ? lastMax + 1 : 1;
    const id = newBracketId();
    onChange([
      ...brackets,
      {
        id,
        weight_min_kg: String(nextMin).replace('.', ','),
        weight_max_kg: String(nextMax).replace('.', ','),
        carrier_price_eur: '8,00',
        sherlook_fee_type: 'percent',
        sherlook_fee_input: '20',
        category_ids: [],
        type_values: [],
      },
    ]);
    setExpandedId(id);
  };

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        bgcolor: 'action.hover',
      }}
    >
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Grille tarifaire {carrierLabel}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {
            'Tranches de poids (kg) : prix transporteur + frais Sherlook (% ou fixe). '
            + 'Sans cat\u00e9gorie assign\u00e9e, la tranche s\'applique \u00e0 toutes les cat\u00e9gories non couvertes. '
            + 'Avec cat\u00e9gorie, vous pouvez restreindre \u00e0 certains types (ex. Chaussures). '
            + 'Priorit\u00e9 : type > cat\u00e9gorie > forfait. Si aucune tranche ne matche, le forfait ci-dessus est utilis\u00e9.'
          }
        </Typography>
      </Box>

      {brackets.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Aucune tranche : le forfait unique s'applique.
        </Typography>
      ) : null}

      {brackets.map((row) => {
        const carrierCents = euroInputToCents(row.carrier_price_eur);
        const feeCents = computeFeeCents(
          carrierCents,
          row.sherlook_fee_type,
          row.sherlook_fee_input
        );
        const totalCents = carrierCents + feeCents;
        const selectedCategories = categories.filter((c) => row.category_ids.includes(c.id));
        const availableTypes = typeOptionsForCategories(categories, row.category_ids);
        const minLabel = row.weight_min_kg.trim() || '0';
        const maxLabel = row.weight_max_kg.trim() || '0';
        const totalLabel = (totalCents / 100).toFixed(2).replace('.', ',');
        const typesLabel =
          row.type_values.length > 0 ? row.type_values.join(', ') : '';
        const catsLabel =
          selectedCategories.length > 0
            ? selectedCategories.map((c) => c.name).join(', ')
              + (typesLabel ? ` \u2014 ${typesLabel}` : '')
            : 'toutes cat\u00e9gories';

        return (
          <Accordion
            key={row.id}
            disableGutters
            expanded={expandedId === row.id}
            onChange={(_, open) => setExpandedId(open ? row.id : false)}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px !important',
              bgcolor: 'background.paper',
              '&:before': { display: 'none' },
              overflow: 'hidden',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                minHeight: 48,
                px: 1.5,
                '& .MuiAccordionSummary-content': {
                  my: 1,
                  alignItems: 'center',
                  gap: 1,
                  overflow: 'hidden',
                },
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }} noWrap>
                Tranche {minLabel} {'\u00e0'} {maxLabel} kg {'\u2014'} {totalLabel} {'\u20ac'}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  ({catsLabel})
                </Typography>
              </Typography>
              <IconButton
                size="small"
                color="error"
                aria-label="Supprimer la tranche"
                onClick={(e) => {
                  e.stopPropagation();
                  removeRow(row.id);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 1.5, pb: 1.5, pt: 0, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                label="De (kg)"
                size="small"
                value={row.weight_min_kg}
                onChange={(e) =>
                  updateRow(row.id, { weight_min_kg: sanitizeDecimalTyping(e.target.value) })
                }
                fullWidth
                inputProps={{ inputMode: 'decimal' }}
              />
              <TextField
                label={'\u00c0 (kg)'}
                size="small"
                value={row.weight_max_kg}
                onChange={(e) =>
                  updateRow(row.id, { weight_max_kg: sanitizeDecimalTyping(e.target.value) })
                }
                fullWidth
                inputProps={{ inputMode: 'decimal' }}
              />
            </Stack>

            <TextField
              label={'Prix transporteur (\u20ac TTC)'}
              size="small"
              value={row.carrier_price_eur}
              onChange={(e) =>
                updateRow(row.id, { carrier_price_eur: sanitizeDecimalTyping(e.target.value) })
              }
              fullWidth
              inputProps={{ inputMode: 'decimal' }}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <FormControl size="small" fullWidth>
                <InputLabel>Frais Sherlook</InputLabel>
                <Select
                  label="Frais Sherlook"
                  value={row.sherlook_fee_type}
                  onChange={(e) => {
                    const nextType = e.target.value as 'percent' | 'fixed';
                    updateRow(row.id, {
                      sherlook_fee_type: nextType,
                      sherlook_fee_input:
                        nextType === 'percent'
                          ? '20'
                          : centsToEuroInput(Math.round(carrierCents * 0.2)),
                    });
                  }}
                >
                  <MenuItem value="percent">Pourcentage du prix transporteur</MenuItem>
                  <MenuItem value="fixed">{'Montant fixe (\u20ac)'}</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label={
                  row.sherlook_fee_type === 'percent'
                    ? 'Frais Sherlook (%)'
                    : 'Frais Sherlook (\u20ac)'
                }
                size="small"
                value={row.sherlook_fee_input}
                onChange={(e) =>
                  updateRow(row.id, {
                    sherlook_fee_input: sanitizeDecimalTyping(e.target.value),
                  })
                }
                fullWidth
                inputProps={{ inputMode: 'decimal' }}
              />
            </Stack>

            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Prix total client : {totalLabel} {'\u20ac'}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                (transporteur {(carrierCents / 100).toFixed(2).replace('.', ',')} {'\u20ac'} + Sherlook{' '}
                {(feeCents / 100).toFixed(2).replace('.', ',')} {'\u20ac'})
              </Typography>
            </Typography>

            <Autocomplete
              multiple
              size="small"
              options={categories}
              value={selectedCategories}
              getOptionLabel={(opt) => opt.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              onChange={(_, value) => {
                const nextIds = value.map((c) => c.id);
                const allowedTypes = new Set(typeOptionsForCategories(categories, nextIds));
                updateRow(row.id, {
                  category_ids: nextIds,
                  type_values: row.type_values.filter((t) => allowedTypes.has(t)),
                });
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.name}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={'Cat\u00e9gories'}
                  placeholder={
                    selectedCategories.length === 0
                      ? 'Toutes (d\u00e9faut si non assign\u00e9es ailleurs)'
                      : undefined
                  }
                />
              )}
            />

            {selectedCategories.length > 0 && availableTypes.length > 0 ? (
              <Autocomplete
                multiple
                freeSolo
                size="small"
                options={availableTypes}
                value={row.type_values}
                onChange={(_, value) =>
                  updateRow(row.id, {
                    type_values: value
                      .map((v) => (typeof v === 'string' ? v.trim() : ''))
                      .filter((v) => v !== ''),
                  })
                }
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={`${option}-${index}`}
                      label={option}
                      size="small"
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Types"
                    placeholder={
                      row.type_values.length === 0
                        ? 'Tous les types de la cat\u00e9gorie'
                        : undefined
                    }
                    helperText={
                      'Ex. Chaussures : laissez vide pour le reste de la cat\u00e9gorie.'
                    }
                  />
                )}
              />
            ) : null}
            </AccordionDetails>
          </Accordion>
        );
      })}

      <Button
        startIcon={<AddIcon />}
        onClick={addRow}
        variant="outlined"
        size="small"
        sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
      >
        Ajouter une tranche
      </Button>
    </Box>
  );
}

/** Convertit les brackets API vers l'etat editable (textes libres). */
export function bracketsFromApi(
  rows: Array<{
    id?: string;
    weight_min_kg?: number;
    weight_max_kg?: number;
    carrier_price_cents?: number;
    sherlook_fee_type?: string;
    sherlook_fee_value?: number;
    category_ids?: number[];
    type_values?: string[];
  }> | null | undefined
): RateBracketRow[] {
  if (!rows?.length) return [];
  return rows.map((row) => {
    const feeType = row.sherlook_fee_type === 'percent' ? 'percent' : 'fixed';
    const feeValue = Number(row.sherlook_fee_value ?? 0);
    return {
      id: row.id || newBracketId(),
      weight_min_kg: String(Number(row.weight_min_kg ?? 0)).replace('.', ','),
      weight_max_kg: String(Number(row.weight_max_kg ?? 0)).replace('.', ','),
      carrier_price_eur: centsToEuroInput(Number(row.carrier_price_cents ?? 0)),
      sherlook_fee_type: feeType,
      sherlook_fee_input:
        feeType === 'percent'
          ? String(feeValue).replace('.', ',')
          : centsToEuroInput(feeValue),
      category_ids: Array.isArray(row.category_ids)
        ? row.category_ids.filter((id) => Number.isFinite(id) && id > 0)
        : [],
      type_values: Array.isArray(row.type_values)
        ? row.type_values
            .filter((v): v is string => typeof v === 'string')
            .map((v) => v.trim())
            .filter((v) => v !== '')
        : [],
    };
  });
}

export function bracketsToApiPayload(rows: RateBracketRow[]) {
  return rows.map((row) => ({
    id: row.id,
    weight_min_kg: parseDecimalInput(row.weight_min_kg),
    weight_max_kg: parseDecimalInput(row.weight_max_kg),
    carrier_price_cents: euroInputToCents(row.carrier_price_eur),
    sherlook_fee_type: row.sherlook_fee_type,
    sherlook_fee_value:
      row.sherlook_fee_type === 'percent'
        ? parseDecimalInput(row.sherlook_fee_input)
        : euroInputToCents(row.sherlook_fee_input),
    category_ids: row.category_ids,
    type_values: row.type_values,
  }));
}
